import React, { useState, createContext, ReactNode, ReactElement } from 'react';
import CustomModal from '../components/Common/CustomModal';
import { FormattedMessage } from 'react-intl';

interface ModalInfo {
    title: ReactElement | string;
    subtitle: ReactElement | string;
    buttonOneText: ReactElement | string;
    buttonTwoText?: ReactElement | string;
    buttonOneAction?: () => void;
    buttonTwoAction?: () => void;
    twoButtons?: boolean;
}

interface CustomModalContext {
    showModal: (info: ModalInfo) => void;
    showNetworkErrorModal: () => void;
    showInvalidUrlModal: () => void;
    showInvalidOtpModal: () => void;
}

interface CustomModalContextProps {
    children: ReactNode;
}

export const CustomModalContext = createContext<CustomModalContext>({} as CustomModalContext);

export const CustomModalProvider = (props: CustomModalContextProps) => {
    const [open, setOpen] = useState<boolean>(false);
    const [modalInfo, setModalInfo] = useState<ModalInfo>({
        title: '',
        subtitle: '',
        buttonOneText: '',
        buttonTwoText: '',
        buttonOneAction: () => console.log('buttonOneAction is not set'),
        buttonTwoAction: () => console.log('buttonTwoAction is not set'),
        twoButtons: false
    });

    const showModal = (info: ModalInfo): void => {
        // setTimeout is used to add a slight delay in between modals shown in rapid succession
        setTimeout(() => {
            setModalInfo({
                ...modalInfo,
                ...info
            });
            setOpen(true);
        }, 100);
    };

    const showNetworkErrorModal = () => {
        showModal({
            title: <FormattedMessage id="modalTitleError" defaultMessage="Error" />,
            subtitle: (
                <FormattedMessage
                    id="modalNetworkSubtitle"
                    defaultMessage="Network connection issue detected. Please verify you have a stable working internet connection and try again."
                />
            ),
            buttonOneText: <FormattedMessage id="ok" defaultMessage="Ok" />
        });
    };

    const showInvalidUrlModal = () => {
        showModal({
            title: <FormattedMessage id="modalTitleError" defaultMessage="Error" />,
            subtitle: (
                <FormattedMessage
                    id="modalInvalidUrlSubtitle"
                    defaultMessage="Invalid URL. Please scan the QR code to open the proper webpage."
                />
            ),
            buttonOneText: <FormattedMessage id="ok" defaultMessage="Ok" />
        });
    };

    const showInvalidOtpModal = () => {
        showModal({
            title: <FormattedMessage id="modalTitleError" defaultMessage="Error" />,
            subtitle: (
                <FormattedMessage
                    id="modalInvalidUrlSubtitle"
                    defaultMessage="Verification code is incorrect. Please try again."
                />
            ),
            buttonOneText: <FormattedMessage id="ok" defaultMessage="Ok" />
        });
    };

    const closeModal = () => {
        setOpen(false);
        setModalInfo({
            title: '',
            subtitle: '',
            buttonOneText: '',
            buttonTwoText: '',
            buttonOneAction: () => console.log('buttonOneAction is not set'),
            buttonTwoAction: () => console.log('buttonTwoAction is not set'),
            twoButtons: false
        });
    };

    return (
        <CustomModalContext.Provider
            value={{
                showModal,
                showNetworkErrorModal,
                showInvalidUrlModal,
                showInvalidOtpModal
            }}>
            {props.children}
            <CustomModal
                open={open}
                handleClose={() => setOpen(false)}
                title={modalInfo.title}
                subtitle={modalInfo.subtitle}
                buttonOneText={modalInfo.buttonOneText}
                buttonTwoText={modalInfo.buttonTwoText || ''}
                buttonOneAction={() => (
                    modalInfo.buttonOneAction
                        ? modalInfo.buttonOneAction()
                        : () => console.log('buttonOneAction is not set'),
                    closeModal()
                )}
                buttonTwoAction={() => (
                    modalInfo.buttonTwoAction
                        ? modalInfo.buttonTwoAction()
                        : () => console.log('buttonTwoAction is not set'),
                    closeModal()
                )}
                twoButtons={modalInfo.twoButtons || false}
            />
        </CustomModalContext.Provider>
    );
};
